import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ModelInfo, ChatMessage, HistoryEntry, FavoriteEntry } from '@shared/types';

// ─────────────────────────────────────────────────────────────────────────────
// Playground state
// ─────────────────────────────────────────────────────────────────────────────

export interface ChatConfig {
  systemPrompt: string;
  temperature: number;
  top_p: number;
  max_tokens: number;
  seed: number | null;
  stream: boolean;
  frequencyPenalty: number;
}

export interface ImageConfig {
  prompt: string;
  negativePrompt: string;
  width: number;
  height: number;
  numSteps: number;
  seed: number | null;
  guidance: number;
}

export interface EmbeddingConfig {
  texts: string[];
}

export interface TranslationConfig {
  text: string;
  sourceLang: string;
  targetLang: string;
}

export interface AudioConfig {
  task: 'transcribe' | 'translate';
  language: string;
}

export interface SummarizationConfig {
  inputText: string;
  maxLength: number;
}

export interface ClassificationConfig {
  text: string;
}

export interface PlaygroundTab {
  id: string;
  modelId: string;
  modelName: string;
  task: string;
  messages: ChatMessage[];
  chatConfig: ChatConfig;
  imageConfig: ImageConfig;
  embeddingConfig: EmbeddingConfig;
  translationConfig: TranslationConfig;
  audioConfig: AudioConfig;
  sumConfig: SummarizationConfig;
  classConfig: ClassificationConfig;
  output: string | Record<string, unknown> | unknown[] | null;
  isLoading: boolean;
  error: string | null;
  timing: { total_ms: number } | null;
  schemaInputs: Record<string, unknown>;
}

function createDefaultTab(model: ModelInfo): PlaygroundTab {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    modelId: model.id,
    modelName: model.name,
    task: model.task,
    messages: model.capabilities.supportsSystemPrompt
      ? [{ role: 'system', content: 'You are a helpful assistant.' }, { role: 'user', content: '' }]
      : [{ role: 'user', content: '' }],
    chatConfig: {
      systemPrompt: 'You are a helpful assistant.',
      temperature: 0.7,
      top_p: 1.0,
      max_tokens: 1024,
      seed: null,
      stream: true,
      frequencyPenalty: 0,
    },
    imageConfig: {
      prompt: '',
      negativePrompt: '',
      width: 1024,
      height: 1024,
      numSteps: 20,
      seed: null,
      guidance: 7.5,
    },
    embeddingConfig: { texts: [''] },
    translationConfig: { text: '', sourceLang: 'en', targetLang: 'es' },
    audioConfig: { task: 'transcribe', language: 'en' },
    sumConfig: { inputText: '', maxLength: 150 },
    classConfig: { text: '' },
    output: null,
    isLoading: false,
    error: null,
    timing: null,
    schemaInputs: {},
  };
}

interface PlaygroundState {
  // Sidebar state
  sidebarOpen: boolean;
  selectedTask: string | null;
  searchQuery: string;

  // Tabs
  tabs: PlaygroundTab[];
  activeTabId: string | null;

  // History and favorites (local cache)
  history: HistoryEntry[];
  favorites: FavoriteEntry[];

  // Comparison mode
  compareMode: boolean;
  compareTabIds: [string, string] | null;

  // UI preferences
  showTokens: boolean;
  showTiming: boolean;

  // Actions
  setSidebarOpen: (open: boolean) => void;
  setSelectedTask: (task: string | null) => void;
  setSearchQuery: (q: string) => void;

  openTab: (model: ModelInfo) => string;
  closeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateTab: (id: string, patch: Partial<PlaygroundTab>) => void;
  duplicateTab: (id: string) => string;

  addMessage: (tabId: string, msg: ChatMessage) => void;
  updateMessage: (tabId: string, index: number, content: string) => void;
  removeMessage: (tabId: string, index: number) => void;
  clearMessages: (tabId: string) => void;

  setOutput: (tabId: string, output: string | Record<string, unknown> | unknown[] | null, timing?: { total_ms: number }) => void;
  setLoading: (tabId: string, loading: boolean) => void;
  setError: (tabId: string, error: string | null) => void;
  appendStreamToken: (tabId: string, token: string) => void;

  addHistoryEntry: (entry: HistoryEntry) => void;
  setHistory: (entries: HistoryEntry[]) => void;
  removeHistoryEntry: (id: string) => void;
  clearHistory: () => void;

  setFavorites: (favs: FavoriteEntry[]) => void;
  addFavorite: (fav: FavoriteEntry) => void;
  removeFavorite: (id: string) => void;

  toggleCompareMode: () => void;
  setCompareTabIds: (ids: [string, string] | null) => void;

  setShowTokens: (v: boolean) => void;
  setShowTiming: (v: boolean) => void;
}

export const usePlaygroundStore = create<PlaygroundState>()(
  persist(
    (set, get) => ({
      sidebarOpen: true,
      selectedTask: null,
      searchQuery: '',
      tabs: [],
      activeTabId: null,
      history: [],
      favorites: [],
      compareMode: false,
      compareTabIds: null,
      showTokens: true,
      showTiming: true,

      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setSelectedTask: (task) => set({ selectedTask: task }),
      setSearchQuery: (q) => set({ searchQuery: q }),

      openTab: (model) => {
        const existing = get().tabs.find((t) => t.modelId === model.id);
        if (existing) {
          set({ activeTabId: existing.id });
          return existing.id;
        }
        const tab = createDefaultTab(model);
        set((s) => ({
          tabs: [...s.tabs, tab],
          activeTabId: tab.id,
        }));
        return tab.id;
      },

      closeTab: (id) => {
        const tabs = get().tabs.filter((t) => t.id !== id);
        const activeTabId = get().activeTabId === id ? (tabs[tabs.length - 1]?.id ?? null) : get().activeTabId;
        set({ tabs, activeTabId });
      },

      setActiveTab: (id) => set({ activeTabId: id }),

      updateTab: (id, patch) => {
        set((s) => ({
          tabs: s.tabs.map((t) => (t.id === id ? { ...t, ...patch } : t)),
        }));
      },

      duplicateTab: (id) => {
        const original = get().tabs.find((t) => t.id === id);
        if (!original) return id;
        const newTab: PlaygroundTab = {
          ...original,
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          output: null,
          isLoading: false,
          error: null,
          timing: null,
        };
        set((s) => ({ tabs: [...s.tabs, newTab], activeTabId: newTab.id }));
        return newTab.id;
      },

      addMessage: (tabId, msg) => {
        set((s) => ({
          tabs: s.tabs.map((t) =>
            t.id === tabId ? { ...t, messages: [...t.messages, msg] } : t,
          ),
        }));
      },

      updateMessage: (tabId, index, content) => {
        set((s) => ({
          tabs: s.tabs.map((t) => {
            if (t.id !== tabId) return t;
            const msgs = [...t.messages];
            msgs[index] = { ...msgs[index]!, content };
            return { ...t, messages: msgs };
          }),
        }));
      },

      removeMessage: (tabId, index) => {
        set((s) => ({
          tabs: s.tabs.map((t) => {
            if (t.id !== tabId) return t;
            return { ...t, messages: t.messages.filter((_, i) => i !== index) };
          }),
        }));
      },

      clearMessages: (tabId) => {
        set((s) => ({
          tabs: s.tabs.map((t) =>
            t.id === tabId ? { ...t, messages: [{ role: 'user', content: '' }], output: null, error: null } : t,
          ),
        }));
      },

      setOutput: (tabId, output, timing) => {
        set((s) => ({
          tabs: s.tabs.map((t) =>
            t.id === tabId
              ? { ...t, output: output as PlaygroundTab['output'], timing: timing ?? t.timing, error: null }
              : t,
          ),
        }));
      },

      setLoading: (tabId, isLoading) => {
        set((s) => ({
          tabs: s.tabs.map((t) => (t.id === tabId ? { ...t, isLoading } : t)),
        }));
      },

      setError: (tabId, error) => {
        set((s) => ({
          tabs: s.tabs.map((t) =>
            t.id === tabId
              ? { ...t, error, ...(error !== null ? { isLoading: false } : {}) }
              : t,
          ),
        }));
      },

      appendStreamToken: (tabId, token) => {
        set((s) => ({
          tabs: s.tabs.map((t) => {
            if (t.id !== tabId) return t;
            const prev = typeof t.output === 'string' ? t.output : '';
            return { ...t, output: prev + token };
          }),
        }));
      },

      addHistoryEntry: (entry) => {
        set((s) => ({ history: [entry, ...s.history].slice(0, 500) }));
      },
      setHistory: (entries) => set({ history: entries }),
      removeHistoryEntry: (id) => {
        set((s) => ({ history: s.history.filter((h) => h.id !== id) }));
      },
      clearHistory: () => set({ history: [] }),

      setFavorites: (favorites) => set({ favorites }),
      addFavorite: (fav) => set((s) => ({ favorites: [fav, ...s.favorites] })),
      removeFavorite: (id) => set((s) => ({ favorites: s.favorites.filter((f) => f.id !== id) })),

      toggleCompareMode: () => set((s) => ({ compareMode: !s.compareMode })),
      setCompareTabIds: (ids) => set({ compareTabIds: ids }),

      setShowTokens: (v) => set({ showTokens: v }),
      setShowTiming: (v) => set({ showTiming: v }),
    }),
    {
      name: 'ai-playground-state',
      // Safe wrapper: silently ignore QuotaExceededError instead of crashing
      storage: {
        getItem: (name) => {
          const v = localStorage.getItem(name);
          return v ? JSON.parse(v) : null;
        },
        setItem: (name, value) => {
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch (e) {
            if (e instanceof DOMException && (e.name === 'QuotaExceededError' || e.code === 22)) {
              console.warn('[store] localStorage quota exceeded – clearing history cache');
              try {
                const existing = JSON.parse(localStorage.getItem(name) ?? '{}') as Record<string, unknown>;
                const state = (existing['state'] ?? {}) as Record<string, unknown>;
                state['history'] = [];
                existing['state'] = state;
                localStorage.setItem(name, JSON.stringify(existing));
              } catch { /* give up gracefully */ }
            }
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
      partialize: (s) => ({
        sidebarOpen: s.sidebarOpen,
        showTokens: s.showTokens,
        showTiming: s.showTiming,
        // history is loaded from the backend API on mount — not persisted locally
        favorites: s.favorites,
      }) as unknown as PlaygroundState,
    },
  ),
);
